import { formatLengthMm, parseLengthToken } from "../units/length.js";
import { MIN_PANEL_DIMENSION_MM } from "./parsePartLine.js";

/**
 * Snap a mm value to the nearest allowed preset when within tolerance.
 * @param {number} mm
 * @param {number[]} snapValues
 * @param {number} [toleranceMm]
 * @returns {number}
 */
export function snapLengthInputMm(mm, snapValues, toleranceMm = 2) {
  if (!snapValues?.length || !Number.isFinite(mm)) {
    return mm;
  }

  let closest = null;
  let closestDist = Infinity;

  for (const candidate of snapValues) {
    const numeric = Number(candidate);
    if (!Number.isFinite(numeric)) {
      continue;
    }
    const dist = Math.abs(numeric - mm);
    if (dist < closestDist) {
      closestDist = dist;
      closest = numeric;
    }
  }

  return closest !== null && closestDist <= toleranceMm ? closest : mm;
}

/**
 * @param {number} currentMm
 * @param {number} presetMm
 * @param {number[]} presets
 * @param {import("../units/length.js").UnitSystem} unitSystem
 */
export function isThicknessPresetSelected(currentMm, presetMm, presets, unitSystem) {
  const current = Number(currentMm);
  const preset = Number(presetMm);
  if (current === preset) {
    return true;
  }
  if (unitSystem !== "imperial") {
    return false;
  }
  return snapLengthInputMm(current, presets) === preset;
}

/**
 * Format mm for editable length fields (metric number or imperial text without trailing quote).
 * @param {number} mm
 * @param {import("../units/length.js").UnitSystem} [unitSystem]
 */
export function formatLengthInputMm(mm, unitSystem = "metric") {
  if (!Number.isFinite(mm)) {
    return "";
  }

  if (unitSystem === "metric") {
    const rounded = Math.round(mm * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  }

  return formatLengthMm(mm, { system: "imperial" }).replace(/"$/, "").trim();
}

/**
 * Parse a single dimension field value to mm.
 * @param {string | number} value
 * @param {{ unitSystem?: import("../units/length.js").UnitSystem, minMm?: number }} [options]
 * @returns {number | null}
 */
export function parseLengthInputMm(value, options = {}) {
  const {
    unitSystem = "metric",
    minMm = MIN_PANEL_DIMENSION_MM,
    snapMmValues,
    snapToleranceMm = 2,
  } = options;
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }

  const parsed = unitSystem === "metric"
    ? Number(raw.replace(",", "."))
    : parseLengthToken(raw, { defaultSystem: unitSystem });

  if (!Number.isFinite(parsed)) {
    return null;
  }

  let mm = parsed;

  if (snapMmValues?.length) {
    mm = snapLengthInputMm(mm, snapMmValues, snapToleranceMm);
  }

  if (unitSystem === "imperial") {
    mm = Math.round(mm);
  }

  if (mm < minMm) {
    return null;
  }

  return mm;
}
